/**
 * useProcurementSignals — convenience hook.
 *
 * Pulls procurement inputs from the canonical contexts and feeds them
 * to the pure resolver. Components MUST consume only this hook (or the
 * resolver directly with explicit inputs) — no inline procurement
 * interpretation in UI.
 */
import { useMemo } from "react";
import type { Job } from "@/data/types";
import { useVendorPO } from "@/data/VendorPOContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import { useOrderDraft } from "@/data/OrderDraftContext";
import { getJobBomReconciliation } from "@/data/materialsReconciliation";
import {
  resolveProcurementSignals,
  type ProcurementOperationalSignals,
} from "@/data/procurement/procurementOperationalSignals";

export function useProcurementSignals(job: Job | null | undefined): ProcurementOperationalSignals {
  const { getPOsByJob, purchaseOrders: allPurchaseOrders } = useVendorPO();
  const { receivingRecords, jobAllocations } = useWarehouseInventory();
  const { packets } = useOrderingPacket();
  const { drafts } = useOrderDraft();

  return useMemo(() => {
    if (!job) return resolveProcurementSignals({ job: null });
    const draft = drafts[job.id];
    const purchaseOrders = draft?.status === "pushed" && draft.pushedPoIds?.length
      ? allPurchaseOrders.filter((po) => draft.pushedPoIds?.includes(po.poId))
      : getPOsByJob(job.id);
    const orderingPackets = packets
      .filter((p) => p.jobId === job.id)
      .flatMap((p) => p.lineItems);
    const jobReceiving = receivingRecords.filter((r) => r.jobId === job.id);
    const jobAllocs = jobAllocations.filter((a) => a.jobId === job.id);
    const reconciliation = getJobBomReconciliation(job);
    return resolveProcurementSignals({
      job,
      purchaseOrders,
      orderingPackets,
      receivingRecords: jobReceiving,
      jobAllocations: jobAllocs,
      materialReconciliation: reconciliation,
    });
  }, [job, drafts, allPurchaseOrders, getPOsByJob, receivingRecords, jobAllocations, packets]);
}
